import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { usePlaid, PlaidLinkMetadata } from '@/hooks/usePlaid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ChevronRight, ChevronLeft, Check, User, Calendar, Target, Building2, Loader2, Wallet, Plus, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { RiskTolerance } from '@/types/portfolio';
import { cn } from '@/lib/utils';
import { usePlaidLink, PlaidLinkOptions, PlaidLinkOnSuccessMetadata } from 'react-plaid-link';

interface OnboardingWizardProps {
  onComplete: () => void;
}

const RISK_OPTIONS: { value: RiskTolerance; label: string; description: string }[] = [
  {
    value: 'Conservative',
    label: 'Conservative',
    description: 'Prioritize capital preservation. Lower risk, potentially lower returns.',
  },
  {
    value: 'Moderate',
    label: 'Moderate',
    description: 'Balance between growth and stability. Medium risk and returns.',
  },
  {
    value: 'Aggressive',
    label: 'Aggressive',
    description: 'Maximize growth potential. Higher risk, potentially higher returns.',
  },
];

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const { user } = useAuth();
  const { updateProfile } = useProfile();
  const { status: plaidStatus, initializePlaidLink, exchangePublicToken, linkedAccounts, fetchLinkedAccounts } = usePlaid();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLinkingAccount, setIsLinkingAccount] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [retirementGoalAmount, setRetirementGoalAmount] = useState('');
  const [retirementTargetAge, setRetirementTargetAge] = useState('');
  const [riskTolerance, setRiskTolerance] = useState<RiskTolerance>('Moderate');

  const totalSteps = 5; // Name, DOB, Retirement Goal, Risk Tolerance, Connect Accounts

  // Initialize Plaid Link token when reaching the connect step
  useEffect(() => {
    if (step === 5 && !linkToken && plaidStatus.configured) {
      initializePlaidLink().then(token => {
        if (token) setLinkToken(token);
      });
    }
  }, [step, linkToken, plaidStatus.configured, initializePlaidLink]);

  // Fetch linked accounts when on step 5
  useEffect(() => {
    if (step === 5) {
      fetchLinkedAccounts();
    }
  }, [step, fetchLinkedAccounts]);

  // Plaid Link success handler
  const handlePlaidSuccess = useCallback(async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    setIsLinkingAccount(true);

    // Convert react-plaid-link metadata to our PlaidLinkMetadata type
    const convertedMetadata: PlaidLinkMetadata = {
      institution: metadata.institution ? {
        name: metadata.institution.name,
        institution_id: metadata.institution.institution_id,
      } : null,
      accounts: metadata.accounts.map(acc => ({
        id: acc.id,
        name: acc.name,
        mask: acc.mask,
        type: acc.type,
        subtype: acc.subtype,
        verification_status: acc.verification_status,
      })),
      link_session_id: metadata.link_session_id,
      transfer_status: metadata.transfer_status || null,
    };

    await exchangePublicToken(publicToken, convertedMetadata);
    await fetchLinkedAccounts();

    // Get a new link token for connecting another account
    const newToken = await initializePlaidLink();
    if (newToken) setLinkToken(newToken);

    setIsLinkingAccount(false);
  }, [exchangePublicToken, fetchLinkedAccounts, initializePlaidLink]);

  // Plaid Link configuration
  const plaidConfig: PlaidLinkOptions = {
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: (err) => {
      if (err) {
        console.error('Plaid Link exited with error:', err);
      }
    },
  };

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink(plaidConfig);

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setSaving(true);

    // Parse retirement goal amount (convert from millions to actual number)
    const goalAmount = retirementGoalAmount
      ? parseFloat(retirementGoalAmount) * 1000000
      : null;

    // Parse retirement target age
    const targetAge = retirementTargetAge
      ? parseInt(retirementTargetAge, 10)
      : null;

    const success = await updateProfile({
      name: name || null,
      date_of_birth: dateOfBirth || null,
      retirement_goal_amount: goalAmount,
      retirement_target_age: targetAge,
      risk_tolerance: riskTolerance,
      onboarding_completed_at: new Date().toISOString(),
    });

    setSaving(false);

    if (success) {
      toast.success('Profile saved!');
      onComplete();
    } else {
      toast.error('Failed to save profile');
    }
  };

  const handleSkip = async () => {
    // Mark onboarding as skipped in DB so it won't show again
    if (user?.id) {
      await updateProfile({
        onboarding_completed_at: new Date().toISOString(),
      });
    }
    onComplete();
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return name.trim().length > 0;
      case 2:
        return true; // Date of birth is optional
      case 3:
        return true; // Retirement goal is optional
      case 4:
        return true; // Risk tolerance has a default
      case 5:
        return true; // Connecting accounts is optional
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg border-border/50">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-2xl font-bold">Outlier</span>
          </div>
          <CardTitle className="text-xl">Let's set up your profile</CardTitle>
          <CardDescription>
            Step {step} of {totalSteps}
          </CardDescription>
          
          {/* Progress bar */}
          <div className="flex gap-2 mt-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  i < step ? 'bg-primary' : 'bg-muted'
                )}
              />
            ))}
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          {/* Step 1: Name */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">What's your name?</h3>
                  <p className="text-sm text-muted-foreground">
                    This helps personalize your portfolio reports.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Step 2: Date of Birth */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">When were you born?</h3>
                  <p className="text-sm text-muted-foreground">
                    Your age helps us provide better retirement planning insights.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth (Optional)</Label>
                <Input
                  id="dob"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 3: Retirement Goal */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">What's your retirement goal?</h3>
                  <p className="text-sm text-muted-foreground">
                    This helps us project your retirement income scenarios.
                  </p>
                </div>
              </div>

              <div className="p-6 rounded-lg border bg-muted/30">
                <div className="flex flex-wrap items-baseline gap-2 text-lg">
                  <span className="text-muted-foreground">I want to retire with</span>
                  <div className="inline-flex items-center gap-1">
                    <span className="text-xl font-semibold">$</span>
                    <Input
                      type="number"
                      value={retirementGoalAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        // Only allow positive numbers with up to 2 decimal places
                        if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                          setRetirementGoalAmount(val);
                        }
                      }}
                      placeholder="1.5"
                      className="w-20 h-10 text-xl font-bold font-mono text-center inline-block"
                      min="0"
                      step="0.1"
                    />
                    <span className="text-xl font-semibold">M</span>
                  </div>
                  <span className="text-muted-foreground">at age</span>
                  <Input
                    type="number"
                    value={retirementTargetAge}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Only allow 2 digit numbers
                      if (val === '' || (/^\d{0,2}$/.test(val) && parseInt(val) <= 99)) {
                        setRetirementTargetAge(val);
                      }
                    }}
                    placeholder="65"
                    className="w-16 h-10 text-xl font-bold font-mono text-center inline-block"
                    min="50"
                    max="99"
                  />
                </div>

                {/* Preview calculation */}
                {retirementGoalAmount && retirementTargetAge && (
                  <div className="mt-4 pt-4 border-t border-border/50 text-sm text-muted-foreground">
                    <p>
                      Target: <span className="font-semibold text-foreground">${(parseFloat(retirementGoalAmount) * 1000000).toLocaleString()}</span> by age{' '}
                      <span className="font-semibold text-foreground">{retirementTargetAge}</span>
                    </p>
                  </div>
                )}
              </div>

              <p className="text-xs text-center text-muted-foreground">
                You can adjust these values later in your profile settings.
              </p>
            </div>
          )}

          {/* Step 4: Risk Tolerance */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Target className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">What's your risk tolerance?</h3>
                  <p className="text-sm text-muted-foreground">
                    This helps us benchmark your portfolio appropriately.
                  </p>
                </div>
              </div>

              <RadioGroup
                value={riskTolerance}
                onValueChange={(value) => setRiskTolerance(value as RiskTolerance)}
                className="space-y-3"
              >
                {RISK_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className={cn(
                      'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
                      riskTolerance === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    )}
                    onClick={() => setRiskTolerance(option.value)}
                  >
                    <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                    <div>
                      <Label htmlFor={option.value} className="font-medium cursor-pointer">
                        {option.label}
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {option.description}
                      </p>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 5: Connect Accounts */}
          {step === 5 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Connect your accounts</h3>
                  <p className="text-sm text-muted-foreground">
                    Link your brokerage accounts to automatically import your portfolio.
                  </p>
                </div>
              </div>

              {/* Linked Accounts List */}
              {linkedAccounts.length > 0 && (
                <div className="space-y-2 mb-4">
                  <Label className="text-sm font-medium">Connected Accounts</Label>
                  <div className="space-y-2">
                    {linkedAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                      >
                        <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{account.institution_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {account.account_name} {account.account_mask ? `••••${account.account_mask}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Connect Button */}
              {plaidStatus.configured ? (
                <Button
                  onClick={() => openPlaidLink()}
                  disabled={!plaidReady || plaidStatus.loading || isLinkingAccount}
                  className="w-full gap-2"
                  variant={linkedAccounts.length > 0 ? 'outline' : 'default'}
                >
                  {isLinkingAccount ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : plaidStatus.loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : linkedAccounts.length > 0 ? (
                    <>
                      <Plus className="h-4 w-4" />
                      Connect Another Account
                    </>
                  ) : (
                    <>
                      <Wallet className="h-4 w-4" />
                      Connect Bank Account
                    </>
                  )}
                </Button>
              ) : (
                <div className="text-center text-sm text-muted-foreground p-4 border rounded-lg bg-muted/30">
                  <p>Bank connection is not available at this time.</p>
                  <p className="mt-1">You can add holdings manually after setup.</p>
                </div>
              )}

              {linkedAccounts.length === 0 && (
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Your data is encrypted and we never store your banking credentials.
                </p>
              )}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            {step < totalSteps ? (
              <Button onClick={handleNext} disabled={!canProceed()} className="gap-1">
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleComplete} disabled={saving} className="gap-1">
                {saving ? 'Saving...' : 'Complete'}
                <Check className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Skip link */}
          <div className="text-center mt-4">
            <button
              onClick={handleSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip for now
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
